"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { initAnalytics } from "@/lib/firebase";

function readStoredUserSnapshot(): {
    id: string | null;
    email: string | null;
    name: string | null;
} {
    if (typeof window === "undefined") {
        return { id: null, email: null, name: null };
    }
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return { id: null, email: null, name: null };
        const u = JSON.parse(raw) as Record<string, unknown>;
        const idRaw = u.id ?? u.userId;
        const id = typeof idRaw === "string" && idRaw.trim() ? idRaw.trim() : null;
        const email = typeof u.email === "string" ? u.email : null;
        const name = typeof u.name === "string" ? u.name : null;
        return { id, email, name };
    } catch {
        return { id: null, email: null, name: null };
    }
}

export default function AnalyticsProvider() {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const lastUserIdRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        const trackPageView = async () => {
            const analytics = await initAnalytics();
            if (analytics) {
                // Track the page view
                logEvent(analytics, "page_view", {
                    page_path: pathname,
                    page_location: window.location.href,
                    page_title: document.title,
                });
            }
        };

        trackPageView();
    }, [pathname, searchParams]);

    useEffect(() => {
        const updateUserId = async () => {
            const { id: currentUserId, email, name } = readStoredUserSnapshot();

            // Only update if the user ID has actually changed
            if (currentUserId !== lastUserIdRef.current) {
                const analytics = await initAnalytics();
                if (analytics) {
                    setUserId(analytics, currentUserId);
                    setUserProperties(analytics, {
                        email: email ?? undefined,
                        name: name ?? undefined,
                    });
                    lastUserIdRef.current = currentUserId;

                }
            }
        };

        updateUserId();

        const onAuth = () => void updateUserId();
        window.addEventListener("auth:login", onAuth);
        window.addEventListener("auth:logout", onAuth);
        window.addEventListener("auth:loginSuccess", onAuth);
        window.addEventListener("storage", onAuth);
        return () => {
            window.removeEventListener("auth:login", onAuth);
            window.removeEventListener("auth:logout", onAuth);
            window.removeEventListener("auth:loginSuccess", onAuth);
            window.removeEventListener("storage", onAuth);
        };
    }, []);

    return null;
}
